import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  TextField,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack,
  Logout,
  Edit,
  MoreVert,
  CheckCircle,
  Cancel,
  AccessTime,
  MoreHoriz,
  Save,
  Close,
  Delete
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import {
  getFormSubmissionById,
  updateSubmissionStatus,
  updateSubmission,
  deleteSubmission
} from '../../services/nocodbService';
import AppTheme from '../../frontend/shared-theme/AppTheme';

const ensureString = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

interface SubmissionDetail {
  Id: string;
  business_name: string;
  business_industry: string;
  business_description: string;
  target_audience: string;
  business_differentials: string;
  assistant_name: string;
  tone_of_voice: string;
  emoji_usage: string;
  client_addressing: string;
  working_hours: string;
  business_location: string;
  google_maps_link: string;
  phone_contact: string;
  email_contact: string;
  website_url: string;
  social_media: string;
  payment_methods: string;
  delivery_areas: string;
  delivery_fee: string;
  delivery_time: string;
  main_objective: string;
  appointment_tool: string;
  forbidden_topics: string;
  transfer_conditions: string;
  transfer_message: string;
  submission_date: string;
  status: string;
  products_services: string;
  faqs: string;
}

const labelMap: Record<string, string> = {
  Id: "ID",
  business_name: "Nome da Empresa",
  business_industry: "Ramo de Atividade",
  business_description: "Descrição do Negócio",
  target_audience: "Público-alvo",
  business_differentials: "Diferenciais",
  assistant_name: "Nome do Assistente",
  tone_of_voice: "Tom de Voz",
  emoji_usage: "Uso de Emojis",
  client_addressing: "Forma de Tratamento",
  working_hours: "Horário de Funcionamento",
  business_location: "Localização",
  google_maps_link: "Link do Google Maps",
  phone_contact: "Telefone",
  email_contact: "E-mail",
  website_url: "Website",
  social_media: "Redes Sociais",
  payment_methods: "Formas de Pagamento",
  delivery_areas: "Áreas de Entrega",
  delivery_fee: "Taxa de Entrega",
  delivery_time: "Prazo de Entrega",
  main_objective: "Objetivo Principal",
  appointment_tool: "Ferramenta de Agendamento",
  forbidden_topics: "Tópicos Proibidos",
  transfer_conditions: "Condições de Transferência",
  transfer_message: "Mensagem de Transferência",
  submission_date: "Data de Submissão",
  status: "Status",
  products_services: "Produtos e Serviços",
  faqs: "Perguntas Frequentes (FAQs)",
};

const emojiUsageMap: Record<string, string> = {
  'yes': 'Sim, pode usar emojis',
  'moderate': 'Usar com moderação',
  'no': 'Não usar emojis'
};

const clientAddressingMap: Record<string, string> = {
  'informal': 'Tratamento informal (você)',
  'formal': 'Tratamento formal (senhor/senhora)'
};

const mainObjectiveMap: Record<string, string> = {
  'answer_faqs': 'Responder perguntas frequentes',
  'qualify_clients': 'Qualificar clientes',
  'schedule_appointments': 'Agendar horários/consultas',
  'sell_products': 'Apresentar produtos/serviços'
};

const SubmissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editableSubmission, setEditableSubmission] = useState<SubmissionDetail | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubmissionDetails = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getFormSubmissionById(id);
        setSubmission(data);
        setEditableSubmission(JSON.parse(JSON.stringify(data)));
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar detalhes da submissão');
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissionDetails();
  }, [id]);

  const handleUpdateStatus = async (newStatus: string) => {
    if (!id || !submission) return;
    try {
      await updateSubmissionStatus(id, newStatus);
      const updatedSubmission = { ...submission, status: newStatus };
      setSubmission(updatedSubmission);
      setEditableSubmission(updatedSubmission);
      setSnackbar({ open: true, message: 'Status atualizado com sucesso!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erro ao atualizar status: ${err.message}`, severity: 'error' });
    } finally {
      setStatusMenuAnchor(null);
    }
  };

  const handleEditClick = () => setIsEditing(true);

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditableSubmission(submission);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (editableSubmission) {
      setEditableSubmission({ ...editableSubmission, [e.target.name]: e.target.value });
    }
  };

  const handleSaveClick = async () => {
    if (!id || !editableSubmission) return;
    setIsSaving(true);
    try {
      await updateSubmission(id, editableSubmission);
      setSubmission(editableSubmission);
      setIsEditing(false);
      setSnackbar({ open: true, message: 'Alterações salvas com sucesso!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Falha ao salvar: ${err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // --- FUNÇÕES PARA CONTROLAR A EXCLUSÃO ---
  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteSubmission(id);
      // Redireciona para o dashboard com uma mensagem de sucesso
      navigate('/admin/dashboard', { state: { message: 'Submissão excluída com sucesso!' } });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Falha ao excluir: ${err.message}`, severity: 'error' });
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  // --- FUNÇÕES AUXILIARES RESTAURADAS ---
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data indisponível';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }).format(date);
    } catch (err) {
      return dateString;
    }
  };

  const renderStatusChip = (status: string) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    let icon = <MoreHoriz />;
    switch (status?.toLowerCase()) {
      case 'pending': color = 'warning'; icon = <AccessTime />; break;
      case 'in_progress': color = 'info'; icon = <MoreHoriz />; break;
      case 'approved': color = 'success'; icon = <CheckCircle />; break;
      case 'rejected': color = 'error'; icon = <Cancel />; break;
      case 'completed': color = 'primary'; icon = <CheckCircle />; break;
    }
    return <Chip label={status || 'desconhecido'} color={color} icon={icon} size="medium" variant="outlined" />;
  };

  const resolveValue = (key: string, value: string): string => {
    if (!value) return '-';
    if (key === 'emoji_usage') return emojiUsageMap[value] || value;
    if (key === 'client_addressing') return clientAddressingMap[value] || value;
    if (key === 'main_objective') return mainObjectiveMap[value] || value;
    return value;
  };

  return (
    <AppTheme>
      <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton size="large" edge="start" color="inherit" sx={{ mr: 2 }} onClick={() => navigate('/admin/dashboard')}><ArrowBack /></IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>Detalhes da Submissão</Typography>
            <IconButton color="inherit" onClick={() => logout()} title="Sair"><Logout /></IconButton>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
          ) : error ? (<Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          ) : submission && editableSubmission ? (
            <>
              <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h5" component="h1" color="primary" sx={{ fontWeight: 600 }}>
                      {submission.business_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Submetido em: {formatDate(submission.submission_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {renderStatusChip(submission.status)}
                    <IconButton onClick={(e) => setStatusMenuAnchor(e.currentTarget)}><MoreVert /></IconButton>
                    <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}>
                      {['pending', 'in_progress', 'approved', 'completed', 'rejected'].map(s =>
                        <MenuItem key={s} onClick={() => handleUpdateStatus(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {Object.keys(labelMap).map((key) => {
                    if (key === 'Id' || key === 'submission_date' || key === 'status') return null;
                    const fieldKey = key as keyof SubmissionDetail;
                    const isLongText = ['business_description', 'target_audience', 'business_differentials', 'products_services', 'faqs', 'transfer_message', 'transfer_conditions', 'forbidden_topics', 'social_media', 'payment_methods', 'delivery_areas', 'working_hours'].includes(key);

                    return (
                      <Grid item xs={12} sm={isLongText ? 12 : 6} key={fieldKey}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            multiline={isLongText}
                            rows={isLongText ? 4 : 1}
                            variant="outlined"
                            label={labelMap[key]}
                            name={fieldKey}
                            value={editableSubmission[fieldKey] || ''}
                            onChange={handleInputChange}
                            disabled={isSaving}
                          />
                        ) : (
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">{labelMap[key]}</Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {resolveValue(key, submission[fieldKey] as string) || '-'}
                              </Typography>
                            </CardContent>
                          </Card>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                <Button variant="outlined" startIcon={<ArrowBack />} component={Link} to="/admin/dashboard">
                  Voltar
                </Button>

                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" color="secondary" startIcon={<Close />} onClick={handleCancelClick} disabled={isSaving}>
                      Cancelar
                    </Button>
                    <Button variant="contained" startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />} onClick={handleSaveClick} disabled={isSaving}>
                      {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleOpenDeleteDialog}>
                      Excluir
                    </Button>
                    <Button variant="contained" startIcon={<Edit />} onClick={handleEditClick}>
                      Editar
                    </Button>
                  </>
                )}
              </Box>
            </>
          ) : (
            <Alert severity="error">Submissão não encontrada</Alert>
          )}
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Você tem certeza que deseja excluir esta submissão? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting} autoFocus>
            {isDeleting ? <CircularProgress size={24} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default SubmissionDetailPage;